import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GraduationCap, GripVertical, Plus, Pencil, Trash2 } from 'lucide-react';
import { educationApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

const STATUSES = ['Completed', 'Pursuing'];
const ADD_OTHER = '__other__';
const blank = { qualification: '', board: '', year: '', score: '', stream: '', status: 'Completed' };

/**
 * Education history for one employee.
 *
 * Records live on the employee document and their array order is the display
 * order, so a drag simply persists the new sequence. The employee's legacy
 * free-text `education` value is never read or written here — it only seeds
 * the very first record's qualification box as a convenience.
 */
export default function EducationSection({ employeeId, canEdit, onChanged }) {
  const [records, setRecords] = useState([]);
  const [quals, setQuals] = useState([]);
  const [legacy, setLegacy] = useState('');
  const [highest, setHighest] = useState('');
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(blank);
  const [customMode, setCustomMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  // Drag state. `dragId` is the row being carried, `overId` the row under it.
  const dragId = useRef(null);
  const [overId, setOverId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await educationApi.list(employeeId);
      setRecords(r.data.records || []);
      setQuals(r.data.qualifications || []);
      setLegacy(r.data.legacy_education || '');
      setHighest(r.data.highest || '');
    } catch { /* a scoped user simply sees nothing */ }
    finally { setLoading(false); }
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const openAdd = () => {
    // The first record starts from whatever free text the record already
    // carries, so HR is not retyping what is already on file.
    setF({ ...blank, qualification: records.length === 0 ? legacy : '' });
    setCustomMode(records.length === 0 && !!legacy && !quals.includes(legacy));
    setEditId(null);
    setOpen(true);
  };

  const openEdit = (r) => {
    setF({ ...blank, ...r });
    setCustomMode(!quals.includes(r.qualification));
    setEditId(r.id);
    setOpen(true);
  };

  const save = async () => {
    if (!f.qualification.trim()) { toast.error('Qualification is required'); return; }
    setBusy(true);
    try {
      if (editId) {
        await educationApi.update(employeeId, editId, f);
        toast.success('Education updated');
      } else {
        await educationApi.add(employeeId, f);
        toast.success('Education added');
      }
      setOpen(false);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    const r = confirmDel;
    setConfirmDel(null);
    try {
      await educationApi.remove(employeeId, r.id);
      toast.success('Education deleted');
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete'); }
  };

  // --- drag & drop -------------------------------------------------------
  const onDrop = async (targetId) => {
    const from = dragId.current;
    dragId.current = null;
    setOverId(null);
    if (!from || from === targetId) return;
    const next = [...records];
    const fi = next.findIndex((r) => r.id === from);
    const ti = next.findIndex((r) => r.id === targetId);
    if (fi < 0 || ti < 0) return;
    next.splice(ti, 0, next.splice(fi, 1)[0]);
    setRecords(next);                                   // optimistic
    try {
      // A reorder can change which qualification counts as the highest.
      await educationApi.reorder(employeeId, next.map((r) => r.id));
      onChanged?.();
    } catch {
      toast.error('Could not save the new order');
      load();                                           // put it back
    }
  };

  const line = (r) => [r.board, r.status === 'Pursuing' ? 'Pursuing' : r.year, r.score]
    .filter(Boolean).join(' • ');

  if (loading) {
    return (
      <div className="bg-white border border-[#E6E9F0] rounded-xl p-6">
        <div className="skeleton h-4 w-32 mb-4" />
        <div className="skeleton h-14 w-full mb-2" />
        <div className="skeleton h-14 w-full" />
      </div>
    );
  }

  return (
    <div data-testid="education-section" className="bg-white border border-[#E6E9F0] rounded-xl p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h2 className="font-display text-xl font-bold tracking-[-0.02em] text-[#172554]">Education</h2>
        {canEdit && (
          <Button data-testid="add-education" size="sm" onClick={openAdd}>
            <Plus className="w-4 h-4" /> Add Education
          </Button>
        )}
      </div>
      <p className="text-[11px] text-[#94A3B8] mb-4">
        {canEdit ? 'Drag a card by its handle to reorder. The order is saved.' : 'Qualifications on file.'}
        {highest && <> The profile&apos;s <b className="text-[#1D4ED8]">Education</b> field shows{' '}
          <b className="text-[#1D4ED8]">{highest}</b> — the highest qualification completed.</>}
      </p>

      {records.length === 0 ? (
        <div className="py-8 text-center">
          <GraduationCap className="w-8 h-8 mx-auto mb-2 text-[#94A3B8]" strokeWidth={1.5} />
          <p className="text-sm font-semibold text-[#172554]">No education records yet</p>
          <p className="text-xs text-[#94A3B8] mt-1">
            {legacy
              ? <>This employee&apos;s record still shows <b className="text-[#1D4ED8]">{legacy}</b> as free text — add it as a record to keep the full history.</>
              : 'Add the first qualification to build the history.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="education-list">
          {records.map((r, i) => (
            <div
              key={r.id}
              data-testid={`education-card-${r.id}`}
              draggable={canEdit}
              onDragStart={() => { dragId.current = r.id; }}
              onDragEnd={() => { dragId.current = null; setOverId(null); }}
              onDragOver={(e) => { e.preventDefault(); if (overId !== r.id) setOverId(r.id); }}
              onDragLeave={() => setOverId((v) => (v === r.id ? null : v))}
              onDrop={(e) => { e.preventDefault(); onDrop(r.id); }}
              style={{ '--d': `${i * 45}ms` }}
              className={`reveal group flex items-start gap-3 rounded-2xl border bg-white/70 p-3 transition-all duration-200
                          ${overId === r.id ? 'border-brand-sky bg-[#F0F9FF]' : 'border-[rgba(37,99,235,0.14)]'}
                          ${canEdit ? 'hover:border-[rgba(37,99,235,0.30)] hover:shadow-soft' : ''}`}
            >
              {canEdit && (
                <button type="button" aria-label="Drag to reorder"
                        data-testid={`education-drag-${r.id}`}
                        className="mt-1 cursor-grab active:cursor-grabbing text-[#94A3B8] hover:text-brand-primary transition-colors">
                  <GripVertical className="w-4 h-4" strokeWidth={2} />
                </button>
              )}

              <span className="icon-tile tile-primary w-9 h-9 mt-0.5">
                <GraduationCap className="w-4 h-4" strokeWidth={2} />
              </span>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-bold text-[#172554]">{r.qualification}</span>
                  {r.status === 'Pursuing' && <span className="pill pill-warn">Pursuing</span>}
                  {r.stream && <span className="text-[11px] text-[#64748B]">{r.stream}</span>}
                </div>
                <p className="text-[12px] text-[#64748B] mt-0.5">{line(r) || '—'}</p>
              </div>

              {canEdit && (
                <div className="flex items-center gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
                  <button type="button" onClick={() => openEdit(r)}
                          data-testid={`education-edit-${r.id}`} title="Edit"
                          className="p-1.5 rounded-lg text-[#64748B] hover:bg-[rgba(56,189,248,0.16)] hover:text-brand-primary transition-colors">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={() => setConfirmDel(r)}
                          data-testid={`education-delete-${r.id}`} title="Delete"
                          className="p-1.5 rounded-lg text-[#64748B] hover:bg-[#FFF1F2] hover:text-[#BE123C] transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* -------- add / edit -------- */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="education-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? 'Edit Education' : 'Add Education'}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold text-[#64748B] mb-1.5 block">Qualification</Label>
              {customMode ? (
                <div className="flex gap-2">
                  <Input data-testid="education-qualification-custom" autoFocus
                         value={f.qualification} onChange={(e) => set('qualification', e.target.value)}
                         placeholder="e.g. M.Tech, MCA, B.Sc, ITI, PhD…" />
                  <Button type="button" variant="outline" size="sm"
                          onClick={() => { setCustomMode(false); set('qualification', ''); }}>
                    Back
                  </Button>
                </div>
              ) : (
                <select data-testid="education-qualification" value={f.qualification}
                        onChange={(e) => {
                          if (e.target.value === ADD_OTHER) { setCustomMode(true); set('qualification', ''); }
                          else set('qualification', e.target.value);
                        }}
                        className="w-full h-10 px-3.5 text-sm">
                  <option value="">Select qualification…</option>
                  {quals.map((q) => <option key={q} value={q}>{q}</option>)}
                  <option value={ADD_OTHER}>+ Add Other Qualification</option>
                </select>
              )}
            </div>

            <div>
              <Label className="text-xs font-semibold text-[#64748B] mb-1.5 block">Board / University</Label>
              <Input data-testid="education-board" value={f.board}
                     onChange={(e) => set('board', e.target.value)} placeholder="CBSE, MDU…" />
            </div>

            <div>
              <Label className="text-xs font-semibold text-[#64748B] mb-1.5 block">Status</Label>
              <select data-testid="education-status" value={f.status}
                      onChange={(e) => set('status', e.target.value)}
                      className="w-full h-10 px-3.5 text-sm">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* A qualification still being read has no result yet. */}
            {f.status !== 'Pursuing' && (
              <>
                <div>
                  <Label className="text-xs font-semibold text-[#64748B] mb-1.5 block">Year of Passing</Label>
                  <Input data-testid="education-year" value={f.year}
                         onChange={(e) => set('year', e.target.value)} placeholder="2024" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#64748B] mb-1.5 block">Percentage / CGPA</Label>
                  <Input data-testid="education-score" value={f.score}
                         onChange={(e) => set('score', e.target.value)} placeholder="82% or 8.4" />
                </div>
              </>
            )}

            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold text-[#64748B] mb-1.5 block">
                Stream / Specialisation <span className="font-normal text-[#94A3B8]">(optional)</span>
              </Label>
              <Input data-testid="education-stream" value={f.stream}
                     onChange={(e) => set('stream', e.target.value)} placeholder="Computer Science" />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="education-save" onClick={save} disabled={busy}>
              {editId ? 'Update Education' : 'Add Education'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* -------- delete confirmation -------- */}
      <Dialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <DialogContent className="max-w-md" data-testid="education-delete-dialog">
          <DialogHeader><DialogTitle>Delete this education record?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#64748B]">
            <b className="text-[#172554]">{confirmDel?.qualification}</b>
            {confirmDel && line(confirmDel) ? ` — ${line(confirmDel)}` : ''} will be removed from this
            employee&apos;s record. This cannot be undone.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button variant="destructive" data-testid="education-delete-confirm" onClick={remove}>Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
